import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieBanner } from "@/components/CookieBanner";
import { queryClient } from "@/lib/queryClient";

import Index from "./pages/Index";
import { ScrollToLocation } from "@/components/layout/ScrollToLocation";
import NotFound from "./pages/NotFound";
const Platform = lazy(() => import("./pages/Platform"));
const Investors = lazy(() => import("./pages/Investors"));
const Advisors = lazy(() => import("./pages/Advisors"));
const Security = lazy(() => import("./pages/Security"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const SampleReport = lazy(() => import("./pages/SampleReport"));
const ForFounders = lazy(() => import("./pages/ForFounders"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const InsightsPolicyRedirect = lazy(() => import("./pages/InsightsPolicyRedirect"));

// The app (login/signup/demo/dashboards) lives on its own subdomain.
const APP_URL = import.meta.env.VITE_APP_URL || "https://app.provenance365.com";

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

/**
 * Full-page bounce to the app subdomain, preserving path + query. Existing marketing
 * CTAs (`<Link to="/auth">`, `navigate("/demo")`, `/auth?plan=...`) land here, so
 * they cross to the app without every call site needing to change.
 */
const AppRedirect = () => {
  useEffect(() => {
    window.location.replace(`${APP_URL}${window.location.pathname}${window.location.search}`);
  }, []);
  return <LoadingFallback />;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <ScrollToLocation />
            <Routes>
              {/* Marketing */}
              <Route path="/" element={<Index />} />
              <Route path="/platform" element={<Platform />} />
              <Route path="/investors" element={<Investors />} />
              <Route path="/advisors" element={<Advisors />} />
              <Route path="/for-founders" element={<ForFounders />} />
              <Route path="/security" element={<Security />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/sample-report" element={<SampleReport />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/insights-beta-terms" element={<InsightsPolicyRedirect document="terms" />} />
              <Route path="/insights-subprocessors" element={<InsightsPolicyRedirect document="subprocessors" />} />

              {/* App entry points → bounce to the app subdomain */}
              <Route path="/auth" element={<AppRedirect />} />
              <Route path="/sign-in" element={<AppRedirect />} />
              <Route path="/sign-up" element={<AppRedirect />} />
              <Route path="/signup" element={<AppRedirect />} />
              <Route path="/demo" element={<AppRedirect />} />
              <Route path="/dashboard" element={<AppRedirect />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <CookieBanner />
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
